/**
 * CENTRALIZED Direction Manager
 * 
 * ⚠️ CRITICAL: This is the ONLY place where dir attribute should be set
 * NEVER set dir attribute in components or anywhere else in the codebase
 * 
 * This prevents:
 * - Race conditions
 * - Inconsistent state
 * - Hard-to-debug issues
 * - Performance problems
 */

/**
 * Set document direction based on language
 * @param {string} language - Language code ('en' or 'ar')
 * @returns {string} - The direction that was set ('ltr' or 'rtl')
 */
export const setDirection = (language) => {
  const isRTL = language === 'ar';
  const direction = isRTL ? 'rtl' : 'ltr';
  
  // Set on document root (affects entire app)
  document.documentElement.dir = direction;
  document.documentElement.lang = language;
  
  // Set on body as well (for CSS selectors)
  if (document.body) {
    document.body.dir = direction;
    document.body.lang = language;
  }
  
  // Add/remove RTL class for CSS targeting
  if (isRTL) {
    document.documentElement.classList.add('rtl');
    document.documentElement.classList.remove('ltr');
  } else {
    document.documentElement.classList.add('ltr');
    document.documentElement.classList.remove('rtl');
  }
  
  // Trigger custom event for components that need to react
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('directionchange', { 
      detail: { direction, language } 
    }));
  }
  
  return direction;
};

/**
 * Get current direction
 * @returns {string} - Current direction ('ltr' or 'rtl')
 */
export const getDirection = () => {
  if (typeof document === 'undefined') return 'ltr';
  return document.documentElement.dir || 'ltr';
};

/**
 * Check if current direction is RTL
 * @returns {boolean} - True if RTL, false if LTR
 */
export const isRTL = () => {
  return getDirection() === 'rtl';
};

/**
 * Get current language from document
 * @returns {string} - Current language code
 */
export const getCurrentLanguage = () => {
  if (typeof document === 'undefined') return 'en';
  return document.documentElement.lang || 'en';
};

